import type { Failure } from "@/modules/shared/failure";
import { renderFieldErrors } from "@/modules/shared/fieldCopy";
import { SYSTEM_ERROR, type FormState } from "@/modules/shared/forms";

/**
 * The **web's** copy for the Meal Plans commands, and the one place a
 * `Failure` becomes a `FormState`. Sentences are specified in
 * `docs/ux/screens/MealPlanEditorScreen.md` and `MealPlanTemplatesScreen.md`.
 *
 * Each refusal says what happened and, where there is one, what to do next,
 * because every one of them is something the world did (a colleague shared or
 * archived the plan first) rather than something the reader did wrong.
 */
export function mealPlansCopyFor(failure: Failure): string {
  switch (failure.code) {
    case "meal_plans_unavailable":
      return "Meal plans are available to clinics listed under Nutritionist.";
    case "customer_not_found":
      return "This customer could not be found.";
    case "meal_plan_not_found":
      return "This meal plan could not be found.";
    case "meal_plan_template_not_found":
      return "That template is already gone.";
    case "meal_plan_archived":
      return "This plan has been archived and can no longer be changed.";
    case "meal_plan_already_shared":
      return "This plan has already been shared with the customer.";
    case "meal_plan_empty":
      return "Add at least one item before sharing the plan.";
    case "meal_plan_not_shared":
      return "Only a shared plan can be archived. A draft can be deleted instead.";
    case "meal_plan_not_deletable":
      return "This plan has been shared, so it can be archived but not deleted.";
    default:
      return SYSTEM_ERROR;
  }
}

export function formStateFor(
  failure: Failure,
  values?: Record<string, string>,
): FormState {
  if (failure.fieldErrors) {
    return { fieldErrors: renderFieldErrors(failure.fieldErrors), values };
  }
  return { formError: mealPlansCopyFor(failure), values };
}
